import React, { useContext, useEffect, useState } from 'react';
import AuthHeader from '../../commons/AuthHeader';
import AuthFooter from '../../commons/AuthFooter';
import { globalContext } from '../../store';
import { getBrandValues, getBrandImages, dispatchToGlobalState, getRaterCountSummary, showSuccessSnackbar } from '../../commons/commonFunctions';
import { Button, ButtonIconAlign, ButtonVariant, IconName } from '@eightfold.ai/octuple';
import { actionButtonStyle } from '../../commons/commonStyles';
import GenerateSection from './GenerateSection';
import { useLocation, useNavigate } from 'react-router-dom';
import ConfirmationModal from '../../commons/ConfirmationModal';
import { clientInfo } from '../../commons/ClientDynamicInfo';
import Api from '../../Api';

// Domain segments exclude dots to prevent backtracking ambiguity (ReDoS)
const emailRegex = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/;

const isValidEmail = (email: string | undefined): boolean => {
  if (!email || typeof email !== 'string') return false;
  const trimmed = email.trim();
  if (trimmed.length > 254) return false; // RFC 5321 max length guard
  return emailRegex.test(trimmed);
};

const InviteRaters = () => {
  const navigate = useNavigate();
  const { globalState, dispatch } = useContext(globalContext);
  const title = globalState?.clientData?.name;
  const location = useLocation();
  const receivedData = location.state;

  const [ratersFieldsData, setRatersFieldsData] = useState<any>();
  const [, setMinTotal] = useState(0);
  const [, setShowLoader] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [continueButtonLoader, setContinueButtonLoader] = useState(false);
  const [cancelButtonLoader, setCancelButtonLoader] = useState(false);
  const [headerLogo, setHeaderLogo] = useState<string>('');
  const [headerImage, setHeaderImage] = useState<string>('');
  const [primaryColor, setPrimaryColor] = useState<string>('#BA3A19');
  const [isRequiredRatersFilled, setIsRequiredRatersFilled] = useState(false);
  const [isMaxRowsFilled, setIsMaxRowsFilled] = useState(false);
  const [raterSummery, setRaterSummary] = useState<any>([]);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [validRaterCount, setValidRaterCount] = useState(0);
  const modalTitle = "Are you sure you're done?";
  const modalBodyText = "You have not invited the minimum required number of raters. You can return to the dashboard, but you must return here to invite the minimum number of raters before taking your self assessment.";
  const modalTitleFilledRequired = "Confirm that you're done inviting raters";
  const modalBodyTextFilledRequired = "Once you confirm, you won't be able to add more raters from this screen. To add more raters, please contact your Leader Portal admin. You can launch your 360 self assessment from the dashboard when you're ready.";
  
  // Set header images and colors based on client branding or fallback to defaults
  const applyClientBranding = () => {
    setHeaderImage(clientInfo[title].headerImage);
    setHeaderLogo(clientInfo[title].headerLogo);
  };

  const applyGlobalBranding = () => {
    const headerLogo = getBrandValues(globalState?.clientData?.branding, "COMPANY_LOGO");
    const headerImg = getBrandValues(globalState?.clientData?.branding, "WELCOME_PAGE_HEADER_IMAGE");
    if (globalState?.headerLogo) {
      setHeaderLogo(globalState?.headerLogo);
    } else {
      getBrandImages(headerLogo, setHeaderLogo, "headerLogo", dispatch, "SET_HEADER_LOGO");
    }

    if (globalState?.headerImg) {
      setHeaderImage(globalState?.headerImg);
    } else {
      getBrandImages(headerImg, setHeaderImage, "headerImage", dispatch, "SET_HEADER_IMG");
    }

    if (globalState?.primaryColor) {
      setPrimaryColor(globalState?.primaryColor);
    } else {
      const primColor = getBrandValues(globalState?.clientData?.branding, 'PRIMARY_COLOR')?.value || '#BA3A19';
      dispatchToGlobalState(setPrimaryColor, primColor, dispatch, 'SET_PRIMARY_COLOR');
    }
  }

  useEffect(() => {
    document.documentElement.scrollTo(0, 0);
    if (clientInfo.hasOwnProperty(title)) {
      applyClientBranding();
    } else {
      applyGlobalBranding();
    }
    
    // Prepare initial rater fields data if receivedData is present
    if (receivedData && receivedData !== null && receivedData !== undefined) {
      // createInitial: stores the initial structure for all rater sections
      // Using Object.create(null) for synk issue 
      const createInitial: any = Object.create(null);

      // preparedConfigData: array of config objects for each rater section
      // Each config contains sectionKey, min, recommended, max, and disableSection flag
      const preparedConfigData = receivedData?.raterDetails?.ratersConfig.map(
        (item: any) => {
          return {
            sectionKey: item.name,      // Section name (e.g., 'Peers', 'Managers')
            min: item.minimum,          // Minimum required raters for this section
            recom: item.recommended,    // Recommended number of raters
            max: item.maximum,          // Maximum allowed raters
            disableSection: false,      // Section enabled by default
          };
        }
      );

      // prepareRatersData: groups existing rater data by type (sectionKey)
      // Example: { 'Peers': [rater1, rater2], 'Managers': [rater3] }
      let prepareRatersData: any = [];
      receivedData?.raterDetails?.ratersData?.map((item: any) => {
        if (prepareRatersData[item.type]) {
          // If array for this type exists, push rater to it
          prepareRatersData[item.type].push(item);
        } else {
          // Otherwise, create new array for this type and add rater
          prepareRatersData[item.type] = [];
          prepareRatersData[item.type].push(item);
        }
      });

      // For each section config, build the rows array for UI
      // Each row represents a rater input (pre-filled if data exists)
      preparedConfigData && preparedConfigData?.forEach((item: any, index: any) => {
        const rows = [];
        // Add recommended number of rows for each section
        let rowsCount = 0;

        if (prepareRatersData[item?.sectionKey]?.length > item?.recom) {
          rowsCount = prepareRatersData[item?.sectionKey]?.length;
        } else {
          rowsCount = item?.recom;
        }
        
        for (let i = 0; i < rowsCount; i++) {
          const raterEntry = prepareRatersData[item?.sectionKey]?.[i];
          // Row is locked if it has a confirmed profile ID or was already sent as a nomination
          const filled = !!(raterEntry?.raterProfileId) || !!(raterEntry?.nominated);

          rows.push({
            // Pre-fill fields if rater data exists for this index
            firstName: raterEntry?.raterFirstName,
            lastName: raterEntry?.raterLastName,
            email: raterEntry?.raterEmail,
            status: raterEntry?.status,
            type: raterEntry?.type,
            raterProfileId: raterEntry?.raterProfileId,
            nominated: raterEntry?.nominated,
            profileType: 'employee',
            rowRequired : i < item?.min,
            // Disable row and Add Raters button if all fields are filled
            disableRow: filled,
            disableAddRatersBtn: filled,
          });
        }

        // Calculate total minimum raters required across all sections
        const minTotal = preparedConfigData.reduce(
          (total: any, item: any) => total + item.min,
          0
        );
        setMinTotal(minTotal);

        // Build initial data structure for this section
        const sectionKey = item?.sectionKey;
        createInitial[sectionKey] = Object.create(null);
        createInitial[sectionKey].rows = rows; // Array of rater rows
        createInitial[sectionKey].otherDetails = {
          ...preparedConfigData[index], // Section config details
        };

        // Set the state for rater fields data (triggers UI render)
        setRatersFieldsData(createInitial);
      });

      let areAllRequiredRowsFilled:any = [];
      let areAllMaxRowsFilled:any = [];
      if (createInitial) {
        Object.keys(createInitial).forEach(sectionKey => {
          const section = createInitial[sectionKey];

          areAllRequiredRowsFilled[sectionKey] = section?.rows
            ?.filter((row: any) => row.rowRequired)
            ?.every((row: any) => row?.firstName && row?.lastName && row?.email && row?.raterProfileId);
          
          // Check if max rows reached for this section (count only filled rows)
          const filledRowsCount = section?.rows?.filter((row: any) => 
            row?.firstName && row?.lastName && row?.email && row?.raterProfileId
          ).length || 0;
          areAllMaxRowsFilled[sectionKey] = filledRowsCount >= section?.otherDetails?.max;
        });
        setIsRequiredRatersFilled(Object.values(areAllRequiredRowsFilled).every((value: any) => value === true));
        setIsMaxRowsFilled(Object.values(areAllMaxRowsFilled).every((value: any) => value === true));
      }
    }
  }, []);

  useEffect(() => {
    let areAllRequiredRowsFilledState:any = [];
    let areAllMaxRowsFilled:any = [];
    if (ratersFieldsData) {
      // Calculate total valid raters (excluding self) from ratersFieldsData
      let totalValidRaters = 0;
      Object.keys(ratersFieldsData).forEach(sectionKey => {
        const section = ratersFieldsData[sectionKey];
        // Count valid raters in this section
        const validRatersInSection = section?.rows?.filter((row: any) => 
          row?.firstName && row?.lastName && isValidEmail(row?.email) //&& row?.raterProfileId
        ).length || 0;
        totalValidRaters += validRatersInSection;
        areAllRequiredRowsFilledState[sectionKey] = section?.rows
          ?.filter((row: any) => row.rowRequired)
          ?.every((row: any) => row?.firstName && row?.lastName && row?.email);
        
        // Check if max rows reached for this section (count only filled rows)
        const filledRowsCount = section?.rows?.filter((row: any) => 
          row?.firstName && row?.lastName && row?.email && row?.raterProfileId
        ).length || 0;
        areAllMaxRowsFilled[sectionKey] = filledRowsCount >= section?.otherDetails?.max;
      });
      setValidRaterCount(totalValidRaters);
      setIsRequiredRatersFilled(Object.values(areAllRequiredRowsFilledState).every((value: any) => value === true));
      setIsMaxRowsFilled(Object.values(areAllMaxRowsFilled).every((value: any) => value === true));
    }
  }, [ratersFieldsData]);

  useEffect(() => {
    if (!globalState?.leaderData?.id) {
      navigate('/dashboard');
    }
  }, [globalState?.leaderData?.id]);

  // Helper function to sync receivedData with latest ratersFieldsData before navigation
  // This ensures newly added raters persist when navigating away and coming back
  const getUpdatedReceivedData = () => {
    // Transform ratersFieldsData back to ratersData array format
    
    // Get self object from receivedData
    const selfObject = receivedData?.raterDetails?.ratersData?.find(
      (rater: any) => rater.type === 'Self' || rater.raterEmail === receivedData?.raterDetails?.focusEmail
    );
    
    const updatedRatersData = ratersFieldsData ? Object.keys(ratersFieldsData).flatMap((type: string) => 
      ratersFieldsData[type].rows
        .filter((row: any) => row.firstName && row.lastName && row.email && row.raterProfileId)
        .map((row: any) => ({
          _id: row._id,
          raterProfileId: row.raterProfileId,
          type: type,
          raterEmail: row.email,
          raterFirstName: row.firstName,
          raterLastName: row.lastName,
          status: row.status || 'Not Started',
          applicationCreatedTs: row.applicationCreatedTs,
          completion: row.completion || '0%',
          assessmentId: row.assessmentId,
          emailEventIds: row.emailEventIds
        }))
    ) : [];
    
    // Add self object at the first index if it exists
    const finalRatersData = selfObject ? [selfObject, ...updatedRatersData] : updatedRatersData;
    return {
      ...receivedData,
      raterDetails: {
        ...receivedData?.raterDetails,
        ratersData: finalRatersData
      }
    };
  };

  // Handler for closing the confirmation modal
  // Resets modal and loader states
  const onClose = () => {
    setShowModal(false);
    setShowLoader(false);
    setContinueButtonLoader(false);
  };

  //Get new raters to invite (those without raterProfileId) and send invite API request
  const getNewRatersToInvite = (toSend?:boolean) => {
    const assessmentDetails = { ...getUpdatedReceivedData() };
    const assessmentDetailsData = assessmentDetails?.raterDetails?.assignmentData;

    return Object.keys(ratersFieldsData).flatMap((type: string) => 
      ratersFieldsData[type].rows
        .filter((row: any) => row.firstName && row.lastName && row.email && !row.raterProfileId)
        .map((row: any) => ({
          assignmentId: assessmentDetailsData?.assignmentId,
          clientId: assessmentDetailsData?.clientId,
          projectId: assessmentDetailsData?.projectId,
          assessmentStructureId: assessmentDetailsData?.assessmentStructureId,
          raterFirstName: row.firstName,
          raterLastName: row.lastName,
          raterEmail: row.email,
          ...(toSend ? { type: type } : { raterGroupName: type }),
          invite: "True"
        }))
    );
  };

  const mergeNewOldRaters = () => {
    const updatedReceivedData = getUpdatedReceivedData();
    let result = { ...updatedReceivedData };
    // Preserve nominated flag for rows already sent so they stay locked on Back navigation
    const ratersWithoutProfileId = getNewRatersToInvite(true).map((r: any) => {
      const alreadyNominated = Object.keys(ratersFieldsData).some((type: string) =>
        ratersFieldsData[type].rows.some(
          (row: any) => row.email === r.raterEmail && row.disableRow && !row.raterProfileId
        )
      );
      return alreadyNominated ? { ...r, nominated: true } : r;
    });
    result.raterDetails.ratersData = [
      ...result.raterDetails.ratersData,
      ...ratersWithoutProfileId
    ];
    return result;
  };

  // Like mergeNewOldRaters but marks newly-sent raters as nominated so they
  // render as disabled when the user returns via "Nominate more raters".
  const mergeNominatedRaters = () => {
    const updatedReceivedData = getUpdatedReceivedData();
    const merged = { ...updatedReceivedData };
    const nominatedRaters = getNewRatersToInvite(true).map((r: any) => ({ ...r, nominated: true }));
    merged.raterDetails.ratersData = [
      ...merged.raterDetails.ratersData,
      ...nominatedRaters
    ];
    return merged;
  };

  // Handler for continuing after confirmation modal
  // Prepares rater data and sends invite API request
  const onContinue = () => {
    setCancelButtonLoader(true);
    const ratersWithoutProfileId = getNewRatersToInvite();
    const updatedReceivedData = getUpdatedReceivedData();
    const assessmentDetails = { ...updatedReceivedData };
    const selfObject = assessmentDetails?.raterDetails?.ratersData?.find(
      (rater: any) => rater.type === 'Self' || rater.raterEmail === assessmentDetails?.raterDetails?.focusEmail
    );
    const payload = {
      action: "inviteRaters",
      source: "leaderPortal",
      requesterUserId: globalState?.leaderData?.id,
      payload: ratersWithoutProfileId
    };

    Api.assignAassessmentAndSendInvitation(payload)
    .then((response: any) => {
      if (response?.response?.data.status === 'success') {
        // Update disableRow to true for rows that match the filter condition
        const updatedFieldsData: any = Object.create(null);
        Object.keys(ratersFieldsData).forEach((type: string) => {
          updatedFieldsData[type] = Object.create(null);
          updatedFieldsData[type].rows = ratersFieldsData[type].rows.map((row: any) => {
            if (row.firstName && row.lastName && row.email && !row.raterProfileId) {
              return { ...row, disableRow: true };
            }
            return row;
          });
          updatedFieldsData[type].otherDetails = { ...ratersFieldsData[type].otherDetails };
        });
        setRatersFieldsData(updatedFieldsData);
        
        showSuccessSnackbar({content: 'Invites are being sent to the raters.'});
        if(selfObject && (selfObject?.status === 'Completed' || selfObject?.status === 'C')){
          setContinueButtonLoader(false);
          setShowModal(false);
        } else {
          const route = '/raterSurveyWelcome';
          navigate(route, {
            state: { ...mergeNominatedRaters(), ratersInvited: true, isMaxRowsFilled, isRequiredRatersFilled },
          });
        }
      }
      setContinueButtonLoader(false);
    })
    .catch((error: any) => {
      console.error(error, 'error');
      setContinueButtonLoader(false);
    });
  };

  const inviteButton=()=>{
    const ratersWithoutProfileId = getNewRatersToInvite();
    if(validRaterCount === 0 || ratersWithoutProfileId.length === 0){
      setErrorMsg('You need at least one rater to continue');
      return;
    }
    
    // Check for invalid emails in all filled rows
    const invalidEmails: string[] = [];
    if (ratersFieldsData) {
      Object.keys(ratersFieldsData).forEach((type: string) => {
        ratersFieldsData[type].rows.forEach((row: any) => {
          // Only validate rows that have some data filled
          if (row.firstName || row.lastName || row.email) {
            if (row.email && !isValidEmail(row.email)) {
              invalidEmails.push(row.email);
            }
          }
        });
      });
    }

    // Transform ratersFieldsData to the format expected by getRaterCountSummary
    // Extract filled rows from each rater type and convert to ratersData format
    const ratersDataFromFields = ratersFieldsData ? Object.keys(ratersFieldsData).flatMap((type: string) => 
      ratersFieldsData[type].rows
        .filter((row: any) => row.firstName && row.lastName && row.email)
        .map((row: any) => ({
          type: type,
          raterFirstName: row.firstName,
          raterLastName: row.lastName,
          raterEmail: row.email,
          raterProfileId: row.raterProfileId
        }))
    ) : [];
    const raterSummary: any = getRaterCountSummary(receivedData?.raterDetails?.ratersConfig, ratersDataFromFields);
    if (invalidEmails.length > 0) {
      setErrorMsg('Please enter valid email addresses for all raters');
      return;
    } else {
      setErrorMsg('');
      if(isRequiredRatersFilled){
        onContinue();
       const route = '/raterSurveyWelcome';
        navigate(route, {
          state: { ...getUpdatedReceivedData(), ratersInvited: true, isMaxRowsFilled, isRequiredRatersFilled },
        });
      } else if (!isRequiredRatersFilled && Object.keys(raterSummary || {}).length === 0) {
        onContinue();
      } else {
        // Generate rater count summary: filled count vs minimum requirement for each type
        setRaterSummary(raterSummary);
        setShowModal(true);
      }
    }
  }

  // Main render: builds the UI for the Invite Raters page
  // Includes header, banner, rater sections, error messages, action buttons, and confirmation modal
  return (
    <div className='mainWrapper' style={{height: ((ratersFieldsData) ? Object.keys(ratersFieldsData)?.length : 0) < 2 ? '100vh': '100%'}}>
      <div className='dashboardPage'>
        {/* Header with client logo and loader */}
        <AuthHeader title={title} headerLogo={headerLogo} setLoader={setShowLoader} />

        <div className='middleWrapper'>
          <div className='dashboardWelcomeText'>
            <div className='authHeaderBannerContent'>
              <Button
                  text="Back"
                  variant={ButtonVariant.SystemUI}
                  classNames='ratersButton'
                  onClick={() => {navigate('/raterSurveyWelcome', { state: mergeNewOldRaters() })}}
                  iconProps={{ path: IconName.mdiChevronLeft }}
                  alignIcon={ButtonIconAlign.Left}
                  style={{padding: '0px 10px 0px 5px'}}
                />
              <p className='authHeaderBannerWelcomeContent'>Assign Raters for <span className='authHeaderBannerWelcomeContentBold'>360 Survey</span></p>
              <p className='welcomContentDesc'>
                Select raters to send 360 survey to. When you’re done, click send nominations below.
              </p>
              {receivedData?.isReviewMode === false && (
                <Button
                  text={"Send Nominations"}
                  variant={ButtonVariant.Secondary}
                  classNames={validRaterCount === 0 ? 'actionButton inactive' : 'actionButton'}
                  style={actionButtonStyle}
                  disabled={validRaterCount === 0}
                  title={validRaterCount === 0 ? 'You need at least one rater to continue' : ''}
                  onClick ={inviteButton}
                  iconProps={{ path: IconName.mdiArrowRight }}
                  alignIcon={ButtonIconAlign.Right}
                />
              )}
              {errorMsg && <div style={{display:'flex', alignItems:'center', gap:'10px', marginTop:'10px'}}>
                <img src='./../assets/img/Alert.svg' alt="Alert Icon"/>
                <span className='apiErrorMessage' style={{color: '#c02195'}}>{errorMsg}</span>
              </div>}
            </div>
          </div>

          <div className='dashboardContentWrapper'>
            <div className='dashboardBackgroundImage' style={{backgroundImage: `url(${headerImage})`}}>
              {/* <img src={headerImage} className='dashboardBGImg' alt="background-image"/> */}
              <div className='dashboardBGOverlay'>
                <div className='dashboardContent'>
                  {ratersFieldsData && (
                    <GenerateSection assignmentData={receivedData?.raterDetails?.assignmentData} fieldsData={ratersFieldsData} fieldState={setRatersFieldsData} primaryColor={primaryColor} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Footer with copyright and links */}
        <AuthFooter />
      </div>
      {/* Confirmation modal for sending invites */}
      {showModal && (
        <ConfirmationModal
          header={(isRequiredRatersFilled) ? modalTitleFilledRequired : modalTitle}
          showModal={showModal}
          bodyText={(isRequiredRatersFilled) ? modalBodyTextFilledRequired : modalBodyText}
          showCancelButton={true}
          onClose={(isRequiredRatersFilled) ? onClose : onContinue}
          onContinue={(isRequiredRatersFilled) ? onContinue : onClose}
          onCloseTop={onClose}
          continueButtonLoader={continueButtonLoader}
          color={primaryColor}
          ratersSummary={raterSummery}
          cancelBtnTxt={'I am done nominating raters'}
          cancelButtonLoader={cancelButtonLoader}
          continueBtnTxt={'Add more raters'}
          isInviteRatersModal={true}
        />
      )}
    </div>
  );
};

export default InviteRaters;
